import axios from "axios";
import { AuthenticationResult } from "@azure/msal-browser";
import { appConfig } from "../../appConfig";
import { getMesToken } from "../../auth/getAccessToken";
import { iPageHeader } from "../../interfaces/iPageHeader";
import { Pager, PageChangeEvent } from "@progress/kendo-react-data-tools";
import { useEffect, useState } from "react";
import { TextBox, TextBoxChangeEvent } from "@progress/kendo-react-inputs";
import { ResourceFileComponent } from "../learningobject/edit/tools/ResourceFIleComponent/ResourceFileComponentTool";

const defaultHeader = {
  currentPage: 0,
  pageSize: 20,
  totalCount: 0,
  totalPages: 0,
  hasNext: false,
  hasPrevious: false,
};

const searchAssets = async (
  currentPage: number,
  pageSize: number,
  searchTerm: string,
  container: "images" | "assets" = "assets"
) => {
  const url = `${appConfig.REACT_APP_MEDIA_API_URL}/api/assets?search=${searchTerm}&page=${currentPage}&size=${pageSize}`;

  // &container=${container}

  const token: AuthenticationResult = await getMesToken();

  let response: any;
  let pageHeader: iPageHeader = defaultHeader;

  try {
    response = await axios.get(url, { headers: { Authorization: `Bearer ${token.accessToken}` } });
    pageHeader = JSON.parse(response.headers["x-pagination"]);
  } catch (error) {
    return { data: [], pageHeader: pageHeader };
  }

  return { data: response.data, pageHeader: pageHeader };
};

const getAsset = async (contentId: string) => {
  const url = `${appConfig.REACT_APP_MEDIA_API_URL}/api/assets/${contentId}`;

  const token: AuthenticationResult = await getMesToken();

  let response: any;

  try {
    response = await axios.get(url, { headers: { Authorization: `Bearer ${token.accessToken}` } });
  } catch (error) {
    return;
  }

  return response.data;
};

const getPropertyValue = (file: any, propertyName: string): string | undefined => {
  const property = file.properties?.find((p: any) => p.name.toLowerCase() === propertyName.toLowerCase());

  return property?.value;
};

const ResourceFile = (props: any) => {
  const [pageHeader, setPageHeader] = useState<iPageHeader>(defaultHeader);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [resourceFiles, setResourceFiles] = useState<ResourceFileComponent[]>([]);
  const [selectedResourceFiles, setSelectedResourceFiles] = useState<ResourceFileComponent[]>([]);

  useEffect(() => {
    setSelectedResourceFiles(props.initialFiles);
  }, []);

  useEffect(() => {
    // Set a timeout to update debounced value after 500ms
    setLoading(true);
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    // Cleanup the timeout if `query` changes before 500ms
    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  useEffect(() => {
    if (debouncedSearchTerm === "") {
      setResourceFiles([]);
      setPageHeader(defaultHeader);
      setLoading(false);
      return;
    }

    setLoading(true);
    searchAssets(pageHeader.currentPage, pageHeader.pageSize, debouncedSearchTerm).then((response) => {
      setResourceFiles(
        response.data.map((file: any) => {
          const name = getPropertyValue(file, "Filename") || "No Name";
          const extension = getPropertyValue(file, "FileExtension") || ".jpg";
          return new ResourceFileComponent(
            `${name.replace(/\.[^/.]+$/, "")}${extension}`,
            file.container,
            extension,
            file.contentId,
            file.versionId
          );
        })
      );

      setPageHeader(response.pageHeader);
      setLoading(false);
    });
  }, [pageHeader.currentPage, pageHeader.pageSize, debouncedSearchTerm]);

  useEffect(() => {
    props.onChange(selectedResourceFiles);
  }, [selectedResourceFiles]);

  const onSearchChange = (e: TextBoxChangeEvent) => {
    const newValue = e.value as string;
    setSearchTerm(newValue!);
  };

  const onPageChange = (event: PageChangeEvent) => {
    const newPage: iPageHeader = { ...pageHeader };
    newPage.pageSize = event.take;
    newPage.currentPage = (event.skip + event.take) / event.take - 1;
    setPageHeader(newPage);
  };

  const onSelectedChange = (file: ResourceFileComponent) => {
    setSelectedResourceFiles((prevSelected) => {
      const exists = prevSelected.some((f) => f.versionId === file.versionId);
      if (exists) {
        return prevSelected.filter((f) => f.versionId !== file.versionId);
      } else {
        return [...prevSelected, file];
      }
    });
  };

  return (
    <div className="col-12">
      <div style={{ margin: 20 }}>
        {selectedResourceFiles.map((file) => (
          <div key={file.versionId} style={{ fontWeight: "bold" }} onClick={() => onSelectedChange(file)}>
            {file.name}
          </div>
        ))}
      </div>
      <div style={{ margin: 20 }}>
        <div>Sök: {loading && "LOADING..."}</div>
        <TextBox
          onChange={(e: TextBoxChangeEvent) => onSearchChange(e)}
          value={searchTerm}
          style={{ width: "300px" }}
          className="k-textbox"
          rounded={"medium"}
        />
      </div>
      <div style={{ margin: 20 }}>
        {resourceFiles
          .filter((selected) => !selectedResourceFiles.some((file) => file.versionId === selected.versionId))
          .map((file) => (
            <div
              key={file.versionId}
              style={{
                fontWeight: selectedResourceFiles.some((f) => f.versionId === file.versionId) ? "bold" : "normal",
              }}
              onClick={() => onSelectedChange(file)}
            >
              {file.name}
            </div>
          ))}
      </div>
      <Pager
        buttonCount={4}
        previousNext={true}
        skip={pageHeader.currentPage * pageHeader.pageSize}
        take={pageHeader.pageSize}
        total={pageHeader.totalCount}
        pageSizes={[10, 20, 30]}
        onPageChange={onPageChange}
      />
    </div>
  );
};

export default ResourceFile;
